import React from 'react';
import { motion } from 'framer-motion';
import LocalizedLink from '../components/common/LocalizedLink';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/common/SEO';
import { 
  FiShield, 
  FiLock, 
  FiUserCheck, 
  FiDatabase, 
  FiEye, 
  FiUsers,
  FiClock,
  FiFileText,
  FiGlobe,
  FiAlertCircle
} from 'react-icons/fi';

const PrivacyPage = () => {
  const { t } = useLanguage();

  const sections = [
    {
      icon: FiUserCheck,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      titleKey: 'pages.privacyIntroTitle',
      contentKey: 'pages.privacyIntro'
    },
    {
      icon: FiDatabase,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      titleKey: 'pages.dataCollectionTitle',
      contentKey: 'pages.dataCollectionDesc',
      subsections: [
        {
          titleKey: 'pages.personalInfoTitle',
          items: ['pages.personalInfoItem1', 'pages.personalInfoItem2', 'pages.personalInfoItem3']
        },
        {
          titleKey: 'pages.petInfoTitle',
          items: ['pages.petInfoItem1', 'pages.petInfoItem2', 'pages.petInfoItem3', 'pages.petInfoItem4']
        },
        {
          titleKey: 'pages.technicalDataTitle',
          items: ['pages.technicalDataItem1', 'pages.technicalDataItem2', 'pages.technicalDataItem3']
        }
      ]
    },
    {
      icon: FiFileText,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      titleKey: 'pages.dataUsageTitle',
      contentKey: 'pages.dataUsageDesc',
      items: [
        'pages.dataUsageItem1',
        'pages.dataUsageItem2',
        'pages.dataUsageItem3',
        'pages.dataUsageItem4',
        'pages.dataUsageItem5',
        'pages.dataUsageItem6',
        'pages.dataUsageItem7'
      ]
    },
    {
      icon: FiLock,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      titleKey: 'pages.dataSecurityTitle',
      contentKey: 'pages.dataSecurityDesc',
      items: [
        'pages.dataSecurityItem1',
        'pages.dataSecurityItem2',
        'pages.dataSecurityItem3',
        'pages.dataSecurityItem4',
        'pages.dataSecurityItem5'
      ],
      note: 'pages.dataSecurityNote'
    },
    {
      icon: FiUsers,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      titleKey: 'pages.dataSharingTitle',
      contentKey: 'pages.dataSharingDesc',
      items: [
        'pages.dataSharingItem1',
        'pages.dataSharingItem2',
        'pages.dataSharingItem3',
        'pages.dataSharingItem4'
      ]
    },
    {
      icon: FiEye,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      titleKey: 'pages.yourRightsTitle',
      contentKey: 'pages.yourRightsDesc',
      items: [
        'pages.yourRightsItem1',
        'pages.yourRightsItem2',
        'pages.yourRightsItem3',
        'pages.yourRightsItem4',
        'pages.yourRightsItem5',
        'pages.yourRightsItem6'
      ],
      note: 'pages.yourRightsNote'
    },
    {
      icon: FiClock,
      iconColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
      titleKey: 'pages.dataRetentionTitle',
      contentKey: 'pages.dataRetentionDesc',
      items: [
        'pages.dataRetentionItem1',
        'pages.dataRetentionItem2',
        'pages.dataRetentionItem3',
        'pages.dataRetentionItem4'
      ],
      note: 'pages.dataRetentionNote'
    },
    {
      icon: FiGlobe,
      iconColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
      titleKey: 'pages.cookiesTitle',
      contentKey: 'pages.cookiesDesc',
      items: [
        'pages.cookiesItem1',
        'pages.cookiesItem2',
        'pages.cookiesItem3'
      ],
      note: 'pages.cookiesNote'
    },
    {
      icon: FiAlertCircle,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      titleKey: 'pages.childrensPrivacyTitle',
      contentKey: 'pages.childrensPrivacyDesc'
    },
    {
      icon: FiGlobe,
      iconColor: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      titleKey: 'pages.internationalTransfersTitle',
      contentKey: 'pages.internationalTransfersDesc'
    },
    {
      icon: FiFileText,
      iconColor: 'text-gray-600',
      bgColor: 'bg-gray-50',
      titleKey: 'pages.policyChangesTitle',
      contentKey: 'pages.policyChangesDesc'
    }
  ];

  return (
    <>
      <SEO 
        title={t('common.seo.privacy.title')}
        description={t('common.seo.privacy.description')}
        keywords={t('common.seo.privacy.keywords')}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 py-20 px-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="mb-6 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="text-4xl" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {t('pages.privacyPolicyTitle')}
              </h1>
              <p className="text-xl text-blue-100 mb-6">
                {t('pages.privacyPolicySubtitle')}
              </p>
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <SafeIcon icon={FiClock} className="text-lg" />
                <span className="text-sm">
                  {t('pages.lastUpdated')}: {t('pages.effectiveDate')}
                </span>
              </div>
            </motion.div>
          </div>
          
          {/* Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="fill-gray-50">
              <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
            </svg>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="p-8">
                    <div className="flex items-start mb-6">
                      <div className={`${section.bgColor} p-4 rounded-xl mr-4 flex-shrink-0`}>
                        <SafeIcon icon={section.icon} className={`text-2xl ${section.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                          {t(section.titleKey)}
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                          {t(section.contentKey)}
                        </p>
                      </div>
                    </div>

                    {/* Subsections (for Data Collection) */}
                    {section.subsections && (
                      <div className="space-y-6 mt-6 ml-20">
                        {section.subsections.map((subsection, subIndex) => (
                          <div key={subIndex} className="border-l-4 border-gray-200 pl-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                              {t(subsection.titleKey)}
                            </h3>
                            <ul className="space-y-2">
                              {subsection.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start">
                                  <span className="text-blue-600 mr-3 mt-1">•</span>
                                  <span className="text-gray-700">{t(item)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Regular Items */}
                    {section.items && !section.subsections && (
                      <ul className="space-y-3 mt-6 ml-20">
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start">
                            <span className={`${section.iconColor} mr-3 mt-1 text-lg`}>•</span>
                            <span className="text-gray-700">{t(item)}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Note */}
                    {section.note && (
                      <div className={`mt-6 ml-20 ${section.bgColor} border-l-4 ${section.iconColor.replace('text', 'border')} p-4 rounded-r-lg`}>
                        <p className="text-sm text-gray-700 italic">
                          <SafeIcon icon={FiAlertCircle} className={`inline mr-2 ${section.iconColor}`} />
                          {t(section.note)}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Contact Section - 移除地址欄位 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg overflow-hidden text-white p-8"
              >
                <div className="flex items-start">
                  <div className="bg-white/20 p-4 rounded-xl mr-4 flex-shrink-0">
                    <SafeIcon icon={FiShield} className="text-2xl" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold mb-3">
                      {t('pages.contactInfoTitle')}
                    </h2>
                    <p className="text-blue-100 mb-6">
                      {t('pages.contactInfoDesc')}
                    </p>
                    <div className="flex items-center">
                      <span className="font-semibold mr-3">{t('pages.contactEmail')}:</span>
                      <a 
                        href="mailto:info@homy.hk" 
                        className="text-blue-200 hover:text-white transition-colors underline"
                      >
                        {t('pages.contactEmailValue')}
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* View Terms Link */}
            <div className="text-center mt-12">
              <LocalizedLink 
                to="/terms" 
                className="inline-flex items-center space-x-2 px-8 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all duration-300 shadow-md hover:shadow-xl"
              >
                <SafeIcon icon={FiFileText} className="text-xl" />
                <span className="font-semibold">{t('pages.viewTerms')}</span>
              </LocalizedLink>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPage;